import { fetchConfig } from '@theme/utilities';

// Use the VARIANT ID of the €0 gift variant, not the product ID.
// Configure via Theme settings → Cart → Automatic free gift.
export const FREE_GIFT_CONFIG = {
  enabled: false,
  giftVariantId: 0,
  requiredQuantity: 2,
  qualifyingMode: 'all_except_gift',
  qualifyingProductHandles: [],
  qualifyingVariantIds: [],
  excludeGiftFromCartCount: true,
  debug: false,
};

/**
 * @param {object} [options]
 * @param {boolean | string} [options.enabled]
 * @param {number | string} [options.giftVariantId]
 * @param {number | string} [options.requiredQuantity]
 * @param {string} [options.qualifyingProductHandles]
 */
export function initFreeGiftConfig(options = {}) {
  if (options.enabled !== undefined) {
    FREE_GIFT_CONFIG.enabled = options.enabled === true || options.enabled === 'true';
  }

  if (options.giftVariantId !== undefined && String(options.giftVariantId).trim() !== '') {
    const id = Number(String(options.giftVariantId).trim());
    if (Number.isFinite(id) && id > 0) {
      FREE_GIFT_CONFIG.giftVariantId = id;
    }
  }

  if (options.requiredQuantity !== undefined && String(options.requiredQuantity).trim() !== '') {
    const qty = Number(options.requiredQuantity);
    if (Number.isFinite(qty) && qty > 0) {
      FREE_GIFT_CONFIG.requiredQuantity = qty;
    }
  }

  const handles = String(options.qualifyingProductHandles ?? '')
    .split(',')
    .map((handle) => handle.trim())
    .filter(Boolean);

  if (handles.length > 0) {
    FREE_GIFT_CONFIG.qualifyingMode = 'product_handles';
    FREE_GIFT_CONFIG.qualifyingProductHandles = handles;
  } else {
    FREE_GIFT_CONFIG.qualifyingMode = 'all_except_gift';
    FREE_GIFT_CONFIG.qualifyingProductHandles = [];
  }

  freeGiftLog('Config initialized', { ...FREE_GIFT_CONFIG });
}

let isSyncingFreeGift = false;

/** @type {string | null} */
let freeGiftSectionId = null;

/** @type {((cart: object | null, sectionHtml?: string | null) => Promise<void>) | null} */
let refreshCartDrawerCallback = null;

/**
 * @param {string} sectionId
 */
export function setFreeGiftSectionId(sectionId) {
  freeGiftSectionId = sectionId;
}

/**
 * @param {(cart: object | null, sectionHtml?: string | null) => Promise<void>} fn
 */
export function setFreeGiftRefreshCallback(fn) {
  refreshCartDrawerCallback = fn;
}

/**
 * @param {...unknown} args
 */
export function freeGiftLog(...args) {
  if (!FREE_GIFT_CONFIG.debug) return;
  console.log('[FreeGift]', ...args);
}

/**
 * @returns {number | null}
 */
function getParsedGiftVariantId() {
  const id = Number(FREE_GIFT_CONFIG.giftVariantId);
  if (!Number.isFinite(id) || id < 1) return null;
  return id;
}

/**
 * @returns {boolean}
 */
export function isFreeGiftConfigValid() {
  if (!FREE_GIFT_CONFIG.enabled) return false;

  const id = getParsedGiftVariantId();
  if (!id) {
    freeGiftLog('Invalid or missing giftVariantId — free gift sync disabled');
    return false;
  }

  return true;
}

/**
 * @returns {Promise<object>}
 */
export async function getCart() {
  const cartUrl =
    typeof Theme !== 'undefined' && Theme.routes?.cart_url ? `${Theme.routes.cart_url}.js` : '/cart.js';

  const response = await fetch(cartUrl, {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Get cart failed: ${response.status} ${errorText}`);
  }

  return response.json();
}

/**
 * @param {object} item
 * @returns {boolean}
 */
export function isGiftLineItem(item) {
  const giftId = getParsedGiftVariantId();
  if (giftId && item.variant_id === giftId) return true;
  return false;
}

/**
 * @param {object} item
 * @returns {boolean}
 */
export function isBundleGiftLineItem(item) {
  const props = item.properties || {};
  return props._bundle === 'free_tank';
}

/**
 * @param {object} cart
 * @returns {object | null}
 */
export function getBundleGiftLineItem(cart) {
  return cart.items?.find((/** @type {object} */ item) => isBundleGiftLineItem(item)) ?? null;
}

/**
 * @param {object} cart
 * @returns {number}
 */
export function getBundlePaidQuantity(cart) {
  if (!cart?.items?.length) return 0;

  return cart.items.reduce((/** @type {number} */ sum, /** @type {object} */ item) => {
    const props = item.properties || {};
    if (props._dore_bundle_offer !== '2-plus-1-free') return sum;
    if (props._bundle === 'free_tank' || props._dore_bundle_slot === 'free') return sum;
    return sum + (Number(item.quantity) || 0);
  }, 0);
}

/**
 * @param {object} cart
 * @returns {object | null}
 */
export function getGiftLineItem(cart) {
  return cart.items?.find((/** @type {object} */ item) => isGiftLineItem(item)) ?? null;
}

/**
 * @param {object} item
 * @returns {boolean}
 */
export function isQualifyingLineItem(item) {
  if (isGiftLineItem(item)) return false;

  const mode = FREE_GIFT_CONFIG.qualifyingMode;

  if (mode === 'variant_ids') {
    const ids = (FREE_GIFT_CONFIG.qualifyingVariantIds || [])
      .map((/** @type {number | string} */ id) => Number(id))
      .filter((/** @type {number} */ id) => id > 0);
    return ids.includes(item.variant_id);
  }

  if (mode === 'product_handles') {
    const handles = (FREE_GIFT_CONFIG.qualifyingProductHandles || []).map((/** @type {string} */ h) =>
      h.toLowerCase()
    );
    const handle = String(item.handle || item.product_handle || '').toLowerCase();
    return Boolean(handle && handles.includes(handle));
  }

  return true;
}

/**
 * @param {object} cart
 * @returns {number}
 */
export function getQualifyingQuantity(cart) {
  if (!cart?.items?.length) return 0;

  return cart.items.reduce((/** @type {number} */ sum, /** @type {object} */ item) => {
    if (!isQualifyingLineItem(item)) return sum;
    return sum + item.quantity;
  }, 0);
}

/**
 * @param {object} cart
 * @returns {boolean}
 */
export function needsFreeGiftChange(cart) {
  const bundleGiftLine = getBundleGiftLineItem(cart);
  if (bundleGiftLine && getBundlePaidQuantity(cart) < 2) return true;

  if (!FREE_GIFT_CONFIG.enabled || !isFreeGiftConfigValid()) return false;

  const qualifyingQty = getQualifyingQuantity(cart);
  const giftLine = getGiftLineItem(cart);
  const qualifies = qualifyingQty >= FREE_GIFT_CONFIG.requiredQuantity;

  return (
    (qualifies && !giftLine) ||
    (!qualifies && Boolean(giftLine)) ||
    (qualifies && giftLine && giftLine.quantity !== 1)
  );
}

/**
 * @returns {boolean}
 */
export function shouldSkipFreeGiftSync() {
  if (!FREE_GIFT_CONFIG.enabled) return true;
  if (!isFreeGiftConfigValid()) return true;
  if (isSyncingFreeGift) return true;
  return false;
}

/**
 * @returns {Record<string, string>}
 */
function getSectionsPayload() {
  if (!freeGiftSectionId) return {};

  return {
    sections: freeGiftSectionId,
    sections_url: window.location.pathname,
  };
}

/**
 * @param {object} response
 * @returns {string | null}
 */
function getSectionHtmlFromResponse(response) {
  if (!freeGiftSectionId || !response?.sections) return null;
  return response.sections[freeGiftSectionId] ?? null;
}

/**
 * @param {string} url
 * @param {RequestInit} options
 * @returns {Promise<object>}
 */
async function postCartJson(url, options) {
  const response = await fetch(url, options);

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Cart request failed: ${response.status} ${errorText}`);
  }

  return response.json();
}

/**
 * @returns {Promise<object>}
 */
export async function addFreeGift() {
  const variantId = getParsedGiftVariantId();
  if (!variantId) throw new Error('Invalid gift variant ID');

  const addUrl =
    typeof Theme !== 'undefined' && Theme.routes?.cart_add_url ? Theme.routes.cart_add_url : '/cart/add.js';

  freeGiftLog('Adding free gift, variant:', variantId);

  return postCartJson(
    addUrl,
    fetchConfig('json', {
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: {
          _free_gift: 'true',
        },
        ...getSectionsPayload(),
      }),
    })
  );
}

/**
 * @param {string} lineKey
 * @returns {Promise<object>}
 */
export async function removeFreeGift(lineKey) {
  freeGiftLog('Removing free gift, key:', lineKey);

  const changeUrl =
    typeof Theme !== 'undefined' && Theme.routes?.cart_change_url
      ? Theme.routes.cart_change_url
      : '/cart/change.js';

  return postCartJson(
    changeUrl,
    fetchConfig('json', {
      body: JSON.stringify({
        id: lineKey,
        quantity: 0,
        ...getSectionsPayload(),
      }),
    })
  );
}

/**
 * @param {string} lineKey
 * @param {number} quantity
 * @returns {Promise<object>}
 */
export async function setGiftQuantity(lineKey, quantity) {
  freeGiftLog('Resetting gift quantity to', quantity);

  const changeUrl =
    typeof Theme !== 'undefined' && Theme.routes?.cart_change_url
      ? Theme.routes.cart_change_url
      : '/cart/change.js';

  return postCartJson(
    changeUrl,
    fetchConfig('json', {
      body: JSON.stringify({
        id: lineKey,
        quantity,
        ...getSectionsPayload(),
      }),
    })
  );
}

/**
 * @param {object | null} cart
 * @param {string | null} [sectionHtml]
 * @returns {Promise<void>}
 */
async function refreshCartDrawerFromCart(cart, sectionHtml = null) {
  if (refreshCartDrawerCallback) {
    await refreshCartDrawerCallback(cart, sectionHtml);
  }
}

/**
 * @param {object} cart
 * @param {object} [options]
 * @returns {Promise<object>}
 */
export async function syncFreeGift(cart, options = {}) {
  if (isSyncingFreeGift) return cart;

  let workingCart = cart;

  try {
    isSyncingFreeGift = true;

    const bundleGiftLine = getBundleGiftLineItem(workingCart);
    if (bundleGiftLine && getBundlePaidQuantity(workingCart) < 2) {
      const changeResponse = await removeFreeGift(bundleGiftLine.key);
      const sectionHtml = getSectionHtmlFromResponse(changeResponse);
      const updatedCart = changeResponse.items ? changeResponse : await getCart();
      await refreshCartDrawerFromCart(updatedCart, sectionHtml);
      workingCart = updatedCart;
      freeGiftLog('Bundle gift removed after paid quantity dropped below 2');
    }

    if (!FREE_GIFT_CONFIG.enabled || !isFreeGiftConfigValid()) return workingCart;

    const qualifyingQty = getQualifyingQuantity(workingCart);
    const giftLine = getGiftLineItem(workingCart);
    const qualifies = qualifyingQty >= FREE_GIFT_CONFIG.requiredQuantity;

    if (!needsFreeGiftChange(workingCart)) {
      freeGiftLog('No gift change needed', options.source || 'unknown');
      return workingCart;
    }

    freeGiftLog('Sync started', options.source || 'unknown', {
      qualifyingQty,
      giftLine: giftLine?.key ?? null,
      qualifies,
    });

    if (qualifies && !giftLine) {
      const addResponse = await addFreeGift();
      const sectionHtml = getSectionHtmlFromResponse(addResponse);
      const updatedCart = await getCart();
      await refreshCartDrawerFromCart(updatedCart, sectionHtml);
      freeGiftLog('Gift added');
      return updatedCart;
    }

    if (!qualifies && giftLine) {
      const changeResponse = await removeFreeGift(giftLine.key);
      const sectionHtml = getSectionHtmlFromResponse(changeResponse);
      const updatedCart = changeResponse.items ? changeResponse : await getCart();
      await refreshCartDrawerFromCart(updatedCart, sectionHtml);
      freeGiftLog('Gift removed');
      return updatedCart;
    }

    if (qualifies && giftLine && giftLine.quantity !== 1) {
      const changeResponse = await setGiftQuantity(giftLine.key, 1);
      const sectionHtml = getSectionHtmlFromResponse(changeResponse);
      const updatedCart = changeResponse.items ? changeResponse : await getCart();
      await refreshCartDrawerFromCart(updatedCart, sectionHtml);
      freeGiftLog('Gift quantity reset');
      return updatedCart;
    }

    return workingCart;
  } catch (error) {
    freeGiftLog('Free gift sync failed safely:', error);

    if (FREE_GIFT_CONFIG.debug) {
      console.warn(
        '[FreeGift] Gift variant may be unavailable, sold out, unpublished, or the wrong ID. Cart drawer remains functional.'
      );
    }

    return workingCart || cart;
  } finally {
    isSyncingFreeGift = false;
  }
}
